import type { User, AuthResponse, AuthTokens, Event } from '../types';

const API_BASE = import.meta.env.VITE_API_URL;

const TOKEN_KEY = 'pelo1-access-token';
const REFRESH_TOKEN_KEY = 'pelo1-refresh-token';

export function getStoredTokens(): AuthTokens | null {
  const accessToken = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

export function setStoredTokens(tokens: AuthTokens): void {
  localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
}

export function clearStoredTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getAuthHeaders(): Record<string, string> {
  const tokens = getStoredTokens();
  if (!tokens) return {};
  return { Authorization: `Bearer ${tokens.accessToken}` };
}

interface RegisterRequest {
  email: string;
  password: string;
  nickname: string;
}

interface LoginRequest {
  email: string;
  password: string;
}

function getRefCode(): string | null {
  const ref = localStorage.getItem('pelo1_ref');
  const expires = localStorage.getItem('pelo1_ref_expires');
  if (!ref || !expires) return null;
  if (Date.now() > Number(expires)) {
    localStorage.removeItem('pelo1_ref');
    localStorage.removeItem('pelo1_ref_expires');
    return null;
  }
  return ref;
}

function clearRefCode(): void {
  localStorage.removeItem('pelo1_ref');
  localStorage.removeItem('pelo1_ref_expires');
}

export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const refCode = getRefCode();

  const response = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...data, refCode }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: '회원가입에 실패했습니다' }));
    throw new Error(error.message || '회원가입에 실패했습니다');
  }

  const result = await response.json();
  setStoredTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken });

  // Clear ref code after successful registration
  if (refCode) clearRefCode();

  return result;
}

export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: '로그인에 실패했습니다' }));
    throw new Error(error.message || '로그인에 실패했습니다');
  }

  const result = await response.json();
  setStoredTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken });
  return result;
}

export async function logout(): Promise<void> {
  const headers = getAuthHeaders();
  if (Object.keys(headers).length > 0) {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers,
    }).catch(() => {});
  }
  clearStoredTokens();
}

export async function refreshTokens(): Promise<AuthTokens | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  });

  if (!response.ok) {
    clearStoredTokens();
    return null;
  }

  const result = await response.json();
  setStoredTokens(result);
  return result;
}

export async function fetchCurrentUser(): Promise<User | null> {
  const headers = getAuthHeaders();
  if (Object.keys(headers).length === 0) return null;

  const response = await fetch(`${API_BASE}/auth/me`, {
    headers,
  });

  if (response.status === 401) {
    const refreshed = await refreshTokens();
    if (!refreshed) return null;

    const retryResponse = await fetch(`${API_BASE}/auth/me`, {
      headers: getAuthHeaders(),
    });

    if (!retryResponse.ok) {
      clearStoredTokens();
      return null;
    }
    return retryResponse.json();
  }

  if (!response.ok) {
    clearStoredTokens();
    return null;
  }

  return response.json();
}

export async function fetchMyEvents(): Promise<Event[]> {
  const headers = getAuthHeaders();
  if (Object.keys(headers).length === 0) return [];

  const response = await fetch(`${API_BASE}/users/me/events`, {
    headers,
  });

  if (!response.ok) {
    throw new Error('내 이벤트 목록을 불러오는데 실패했습니다');
  }

  return response.json();
}

export function getGoogleLoginUrl(): string {
  return `${API_BASE}/auth/google`;
}

export function handleOAuthCallback(accessToken: string, refreshToken: string): void {
  setStoredTokens({ accessToken, refreshToken });
}

// Device management APIs
export interface Device {
  id: string;
  deviceId: string;
  deviceCode: string;
  deviceName: string | null;
  registeredAt: string;
  lastSeenAt: string;
}

export async function fetchMyDevices(): Promise<Device[]> {
  const headers = getAuthHeaders();
  if (Object.keys(headers).length === 0) return [];

  const response = await fetch(`${API_BASE}/users/me/devices`, {
    headers,
  });

  if (!response.ok) {
    throw new Error('디바이스 목록을 불러오는데 실패했습니다');
  }

  return response.json();
}

export async function linkDevice(deviceCode: string, name?: string): Promise<Device> {
  const headers = getAuthHeaders();

  const response = await fetch(`${API_BASE}/users/me/devices`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ deviceCode, name }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: '디바이스 연결에 실패했습니다' }));
    throw new Error(error.message || '디바이스 연결에 실패했습니다');
  }

  return response.json();
}

export async function unlinkDevice(deviceId: string): Promise<void> {
  const headers = getAuthHeaders();

  const response = await fetch(`${API_BASE}/users/me/devices/${deviceId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    throw new Error('디바이스 연결 해제에 실패했습니다');
  }
}

export async function updateDeviceName(deviceId: string, name: string): Promise<Device> {
  const headers = getAuthHeaders();

  const response = await fetch(`${API_BASE}/users/me/devices/${deviceId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    throw new Error('디바이스 이름 변경에 실패했습니다');
  }

  return response.json();
}

// Profile update API
interface UpdateProfileRequest {
  nickname?: string;
  profileImage?: string;
}

interface UpdateProfileResponse {
  id: string;
  email: string;
  nickname: string;
  profileImage: string | null;
}

export async function updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
  const headers = getAuthHeaders();

  const response = await fetch(`${API_BASE}/users/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: '프로필 업데이트에 실패했습니다' }));
    throw new Error(error.message || '프로필 업데이트에 실패했습니다');
  }

  return response.json();
}
